import { debounce } from "lodash";
import { Box, Typography, IconButton, Badge, Card, Grid } from "@mui/joy";
import { Minus, Plus } from "lucide-react";
import { useEffect, useState, useRef } from "react";

import { useMeasurement, useInspectionAttributes } from "../../../hooks/query";
import { useProject } from "../../../hooks";
import { TearOff } from "./TearOff";
import { ColorSelection } from "./ColorSelection";
import { RoofPitch } from "./RoofPitch";
import { VentilationType } from "./VentilationType";
import { VentilationPainted } from "./VentilationPainted";
import { ValleyType } from "./ValleyType";
import { ChimneyType } from "./ChimneyType";
import { ChimneyColor } from "./ChimneyColor";
import { EaveDripEdge } from "./EaveDripEdge";
import { RakeDripEdge } from "./RakeDripEdge";
import { FurnaceVentJackSize } from "./FurnaceVentJackSize";
import { FurnaceVentCapCollar } from "./FurnaceVentCapCollar";
import { ModeOfPayment } from "./ModeOfPayment";
import { ChimneySize } from "./ChimneySize";
import { SkylightType } from "./SkylightType";
import { SkylightSize } from "./SkylightSize";
import { EvaporativeCooler } from "./EvaporativeCooler";
import { GutterGuardType } from "./GutterGuardType";

// Define all standard measurements we track
const standardVisualMeasurement = [
    "Roof Vent",
    "Pipe Jack",
    "Furnace Vents",
    "Bathroom Vents",
    "Dryer Vents",
    "Split Boots",
    "Headwall",
    "Roll Metal",
    "Soffit Length",
    "Chimney",
    "Skylights",
    "Open Patio/Soffits",
    "Satellite Dish",
    "Solar Panels",
];

// Define groups for counters
const measurementGroups: Record<string, string[]> = {
    Vents: ["Roof Vent", "Furnace Vents", "Bathroom Vents", "Dryer Vents"],
    Penetrations: ["Pipe Jack", "Split Boots", "Chimney", "Skylights", "Satellite Dish", "Solar Panels"],
    "Structure & Metal": ["Headwall", "Roll Metal", "Soffit Length", "Open Patio/Soffits"],
};

type Measurement = {
    Id: string;
    ActualValue: number;
    [key: string]: any;
};

interface IVisualMeasurement {
    measurements: any;
    setMeasurements: (e: any) => void;
    attributes: any;
    setAttributes: (e: any) => void;
}

export function VisualMeasurement(props: IVisualMeasurement) {
    const { measurements, setMeasurements, attributes, setAttributes } = props;
    const [measurementData, setMeasurementData] = useState<any[]>([]);

    const { useUpdateSingleMeasurement } = useMeasurement();
    const updateMeasurementSvc = useUpdateSingleMeasurement();

    // -- Measurement Updates Logic --
    const debouncedApiUpdate = useRef(
        debounce(async (recordId: string, newValue: number) => {
            try {
                const params = { Id: recordId, ActualValue: Number(newValue) };
                await updateMeasurementSvc.mutateAsync(params);
                setMeasurements((prev: Measurement[]) =>
                    prev.map((m) => (m.Id === recordId ? { ...m, ActualValue: Number(newValue) } : m)),
                );
            } catch (error) {
                console.error(error);
            }
        }, 500),
    ).current;

    const handleMeasurementUpdate = async (eventType: "remove" | "add", event: any) => {
        const delta = eventType === "add" ? 1 : -1;
        setMeasurementData((prev) =>
            prev.map((item) => {
                if (item.name === event.name) {
                    const updatedActual = Math.max(0, item.actual + delta);
                    debouncedApiUpdate(item.id, updatedActual);
                    return { ...item, actual: updatedActual };
                }
                return item;
            }),
        );
    };

    const processData = () => {
        if (!measurements || measurements.length === 0) return;
        const newData = standardVisualMeasurement.map((item: any) => {
            const entry = measurements.find((entry: any) => entry.MeasurementType === item);
            if (!entry) return { name: item, total: null, actual: null, id: null };
            return {
                ...item,
                name: entry.MeasurementType,
                total: Number(entry.CalculatedValue),
                actual: Number(entry.ActualValue),
                id: entry.Id,
            };
        });
        setMeasurementData(newData);
    };

    useEffect(() => {
        processData();
        return () => {
            debouncedApiUpdate.cancel();
        };
    }, [measurements]);

    const renderMeasurementItem = (item: any) => (
        <Box
            key={item.name}
            sx={{
                display: "flex",
                borderRadius: "40px",
                p: 0.2,
                pb: 0.15,
                alignItems: "center",
                gap: 1,
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <IconButton
                variant="soft"
                size="sm"
                color="danger"
                onClick={() => handleMeasurementUpdate("remove", item)}
                sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    "--IconButton-size": "32px",
                    borderRadius: "50%",
                }}
            >
                <Minus size={12} />
            </IconButton>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0 }} component={"span"}>
                {item.name}
            </Typography>
            <Badge badgeContent={item.actual || 0} variant="soft" color="warning">
                <IconButton
                    variant="soft"
                    color="success"
                    size="sm"
                    onClick={() => handleMeasurementUpdate("add", item)}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        "--IconButton-size": "32px",
                        borderRadius: "50%",
                    }}
                >
                    <Plus size={12} />
                </IconButton>
            </Badge>
        </Box>
    );

    return (
        <Box sx={{}}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 1 }}>
                Visual Measurements
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {Object.entries(measurementGroups).map(([groupName, groupItems]) => {
                    const itemsInGroup = measurementData.filter((item) => groupItems.includes(item.name));
                    if (itemsInGroup.length === 0) return null;

                    return (
                        <Card key={groupName} variant="outlined" sx={{ p: 2 }}>
                            <Typography level="title-sm" sx={{ fontWeight: "lg", mb: 2 }}>
                                {groupName}
                            </Typography>
                            <Box display={"flex"} gap={1} flexWrap={"wrap"} rowGap={2}>
                                {itemsInGroup.map((item) => renderMeasurementItem(item))}
                            </Box>
                        </Card>
                    );
                })}
            </Box>
            <AdditionalAttributes attributes={attributes} setAttributes={setAttributes} />
        </Box>
    );
}

function AdditionalAttributes(props: { attributes: any; setAttributes: any }) {
    const { attributes, setAttributes } = props;
    const { project } = useProject();
    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateAttributeSvc = useUpdateSingleAttribute();

    const handleUpdate = async (value: string, attributeName: string) => {
        try {
            if (!project?.id) {
                console.error(`Cannot update ${attributeName}: Project ID not found.`);
                return;
            }
            const attribute = attributes?.find((attr: any) => attr.AttributeName === attributeName);
            const attributeId = attribute?.Id;

            const updateParams: any = { Value: value };
            if (attributeId) {
                updateParams.Id = attributeId;
            } else {
                updateParams.ProjectId = project.id;
                updateParams.AttributeName = attributeName;
            }

            const response = await updateAttributeSvc.mutateAsync(updateParams);

            if (attributeId) {
                setAttributes((prev: any[]) =>
                    prev.map((attr) =>
                        attr.AttributeName === attributeName ? { ...attr, AttributeValue: value } : attr,
                    ),
                );
            } else {
                const newAttribute = {
                    Id: response?.Id || attribute?.Id,
                    AttributeName: attributeName,
                    AttributeValue: value,
                };
                setAttributes((prev: any[]) => {
                    const exists = prev.some((attr) => attr.AttributeName === attributeName);
                    if (!exists) return [...prev, newAttribute];
                    return prev.map((attr) =>
                        attr.AttributeName === attributeName ? { ...attr, ...newAttribute } : attr,
                    );
                });
            }
        } catch (error) {
            console.error(`Error updating ${attributeName}:`, error);
        }
    };

    const getAttrValue = (name: string) => attributes?.find((attr: any) => attr.AttributeName === name)?.AttributeValue;

    return (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ fontWeight: "lg" }}>
                    Roof
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={12} sm={6} md={4}>
                        <ColorSelection
                            value={getAttrValue("Roof Color")}
                            onChange={(v) => handleUpdate(v, "Roof Color")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <RoofPitch value={getAttrValue("Roof Pitch")} onChange={(v) => handleUpdate(v, "Roof Pitch")} />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <TearOff
                            value={getAttrValue("Tear Off Layer")}
                            onChange={(v) => handleUpdate(v, "Tear Off Layer")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <VentilationType
                            value={getAttrValue("Ventilation Type")}
                            onChange={(v) => handleUpdate(v, "Ventilation Type")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <VentilationPainted
                            value={getAttrValue("Ventilation Painted")}
                            onChange={(v) => handleUpdate(v, "Ventilation Painted")}
                        />
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ fontWeight: "lg" }}>
                    Furnace Vent
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                        <FurnaceVentJackSize
                            value={getAttrValue("Furnace Vent Jack Size")}
                            onChange={(v) => handleUpdate(v, "Furnace Vent Jack Size")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6}>
                        <FurnaceVentCapCollar
                            value={getAttrValue("Furnace Vent Cap and Collar")}
                            onChange={(v) => handleUpdate(v, "Furnace Vent Cap and Collar")}
                        />
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ fontWeight: "lg" }}>
                    Drip Edge
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                        <EaveDripEdge
                            value={getAttrValue("Eave Drip Edge")}
                            onChange={(v) => handleUpdate(v, "Eave Drip Edge")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6}>
                        <RakeDripEdge
                            value={getAttrValue("Rake Drip Edge")}
                            onChange={(v) => handleUpdate(v, "Rake Drip Edge")}
                        />
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ fontWeight: "lg" }}>
                    Components & Equipment
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={12} sm={6} md={4}>
                        <ChimneySize
                            value={getAttrValue("Chimney Size")}
                            onChange={(v) => handleUpdate(v, "Chimney Size")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <SkylightType
                            value={getAttrValue("Skylight Type")}
                            onChange={(v) => handleUpdate(v, "Skylight Type")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <SkylightSize
                            value={getAttrValue("Skylight Size")}
                            onChange={(v) => handleUpdate(v, "Skylight Size")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <EvaporativeCooler
                            value={getAttrValue("Has Evaporative Cooler")}
                            onChange={(v) => handleUpdate(v, "Has Evaporative Cooler")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <GutterGuardType
                            value={getAttrValue("Gutter Guard Type")}
                            onChange={(v) => handleUpdate(v, "Gutter Guard Type")}
                        />
                    </Grid>
                </Grid>
            </Card>

            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-sm" sx={{ fontWeight: "lg" }}>
                    Other
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                        <ValleyType
                            value={getAttrValue("Valley Type")}
                            onChange={(v) => handleUpdate(v, "Valley Type")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6}>
                        <ChimneyType
                            value={getAttrValue("Chimney Type")}
                            onChange={(v) => handleUpdate(v, "Chimney Type")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6}>
                        <ChimneyColor
                            value={getAttrValue("Chimney Color")}
                            onChange={(v) => handleUpdate(v, "Chimney Color")}
                        />
                    </Grid>
                    <Grid xs={12} sm={6}>
                        <ModeOfPayment
                            value={getAttrValue("Mode of Payment")}
                            onChange={(v) => handleUpdate(v, "Mode of Payment")}
                        />
                    </Grid>
                </Grid>
            </Card>
        </Box>
    );
}
